import 'react-datepicker/dist/react-datepicker.css';

import { ClearOutlined } from '@mui/icons-material';
import { IconButton, InputAdornment, TextField } from '@mui/material';
import { DatePicker, DateTimePicker } from '@mui/x-date-pickers';
import _ from 'lodash';
import { DateTime } from 'luxon';
import { useRef } from 'react';
import type { RegisterOptions } from 'react-hook-form';
import { Controller, useFormContext } from 'react-hook-form';

import type { VNFieldHelpProps } from '@/components/VNFieldHelp';
import {
  hasFieldHelpIcon,
  useVNFieldHelpContext,
  VNFieldHelpIconButton,
  VNFieldHelpLayout,
  VNFieldTopLabel,
} from '@/components/VNFieldHelp';
import { dateUtils } from '@/utils/dateUtils';

type Props = Omit<VNFieldHelpProps, 'helperText'> & {
  name: string;
  label: string;
  timezone?: string;
  required?: boolean;
  rules?: RegisterOptions;
  onDateChanged?: (oldValue, newValue) => void;
  disabled?: boolean;
  inputFormat?: string;
  minDate?: Date | string;
  maxDate?: Date | string;
  hideTime?: boolean;
  allowClear?: boolean;
  /** Static helper text below the field (distinct from clickable field-help helperText). */
  helperText?: string;
  /** false (default) = floating label; true = static label above the control. */
  ver2?: boolean;
};

type DatePickerFieldProps = {
  name: string;
  label: string;
  timezone?: string;
  required: boolean;
  rules?: RegisterOptions;
  onDateChanged?: (oldValue, newValue) => void;
  disabled: boolean;
  inputFormat?: string;
  minDate?: Date | string;
  maxDate?: Date | string;
  hideTime: boolean;
  allowClear: boolean;
  helperText?: string;
  resolvedFormat: string;
  ver2: boolean;
};

const VNDatePickerField = ({
  name,
  label,
  timezone,
  required,
  rules,
  onDateChanged,
  disabled,
  minDate,
  maxDate,
  hideTime,
  allowClear,
  helperText,
  resolvedFormat,
  ver2,
}: DatePickerFieldProps) => {
  const { control } = useFormContext();
  const inputEl = useRef<HTMLInputElement>(null);
  const { iconConfig } = useVNFieldHelpContext();

  const parseDateInput = (d: any) => {
    if (!d) return undefined;
    const d1 = DateTime.fromJSDate(d).setZone(timezone);
    return dateUtils.toDate(d1, timezone);
  };

  return (
    <Controller
      name={name}
      control={control}
      rules={{
        required: required && 'This is a required field',
        ...rules,
      }}
      render={({ field: { onChange, value, ...field }, fieldState }) => {
        const displayedDate = dateUtils.toJSDate(value, timezone);
        const parsedMinDate = parseDateInput(minDate);
        const parsedMaxDate = parseDateInput(maxDate);

        const PickerComponent = hideTime ? DatePicker : DateTimePicker;
        const dateConstraints = hideTime
          ? { minDate: parsedMinDate, maxDate: parsedMaxDate }
          : { minDateTime: parsedMinDate, maxDateTime: parsedMaxDate };

        return (
          <PickerComponent
            ref={inputEl}
            value={displayedDate}
            disabled={disabled}
            {...dateConstraints}
            onChange={(date) => {
              const oldValue = _.cloneDeep(value);
              const newValue = dateUtils.toString(date, timezone);
              onChange(newValue);
              if (onDateChanged) {
                onDateChanged(oldValue, newValue);
              }
            }}
            label={ver2 ? undefined : `${label}${required ? ' *' : ''}`}
            inputFormat={resolvedFormat}
            className="vn-datepicker-input"
            renderInput={(params) => (
              <TextField
                size="small"
                hiddenLabel={ver2}
                value={displayedDate}
                autoComplete="off"
                {...params}
                {...field}
                error={!!fieldState.error}
                helperText={fieldState.error?.message || helperText}
                InputProps={{
                  ...params.InputProps,
                  endAdornment: (
                    <>
                      {allowClear && value && !disabled && (
                        <IconButton
                          size="small"
                          className="vn-datepicker-clear"
                          onClick={() => onChange(null)}
                          aria-label="Clear"
                          edge="end"
                        >
                          <ClearOutlined fontSize="small" />
                        </IconButton>
                      )}
                      {params.InputProps?.endAdornment}
                      {iconConfig && (
                        <InputAdornment position="end" className="vn-field-help__input-adornment">
                          <VNFieldHelpIconButton {...iconConfig} />
                        </InputAdornment>
                      )}
                    </>
                  ),
                }}
              />
            )}
          />
        );
      }}
    />
  );
};

const VNDatePicker = ({
  name,
  label,
  timezone,
  required = false,
  rules,
  onDateChanged = null,
  disabled = false,
  inputFormat,
  minDate,
  maxDate,
  hideTime = false,
  allowClear = false,
  helperText,
  ver2 = false,
  ...fieldHelpProps
}: Props) => {
  const resolvedFormat = inputFormat || (hideTime ? 'MM/dd/yyyy' : 'MM/dd/yyyy hh:mm a');
  const { watch } = useFormContext();
  const currentValue = watch(name);
  const showFieldHelp = hasFieldHelpIcon(fieldHelpProps);

  const fieldProps: DatePickerFieldProps = {
    name,
    label,
    timezone,
    required,
    rules,
    onDateChanged,
    disabled,
    inputFormat,
    minDate,
    maxDate,
    hideTime,
    allowClear,
    helperText,
    resolvedFormat,
    ver2,
  };

  const field = <VNDatePickerField {...fieldProps} />;

  return (
    <div className={`vn-datepicker${ver2 ? ' vn-field--label-above' : ''}`}>
      {ver2 && label ? <VNFieldTopLabel label={label} required={required} htmlFor={name} /> : null}
      {showFieldHelp ? (
        <VNFieldHelpLayout
          label={label}
          required={required}
          iconAttach="adornment"
          className={['vn-field-help--datepicker', allowClear && currentValue ? 'vn-field-help--datepicker-clear' : '']
            .filter(Boolean)
            .join(' ')}
          {...fieldHelpProps}
        >
          {field}
        </VNFieldHelpLayout>
      ) : (
        field
      )}
    </div>
  );
};

export default VNDatePicker;
